import { useEffect } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "@/shared/hooks/use-toast";
import {
  type ReceivableShareConfig,
  type PaginatedReceivableShareConfigsResponse,
  type GetReceivableShareConfigsParams,
  type CreateReceivableShareConfigPayload,
  type UpdateReceivableShareConfigPayload,
} from "../utils/types";
import { receivableShareConfigApi } from "../api";
import { receivableShareConfigKeys } from "../utils/share-config.keys";

export function useReceivableShareConfigs(
  filters?: GetReceivableShareConfigsParams,
  options?: Omit<UseQueryOptions<PaginatedReceivableShareConfigsResponse, Error>, "queryKey" | "queryFn">,
) {
  const query = useQuery({
    queryKey: receivableShareConfigKeys.list(filters),
    queryFn: ({ signal }) => receivableShareConfigApi.list(filters, signal),
    staleTime: 60_000,
    ...(options as Omit<UseQueryOptions<PaginatedReceivableShareConfigsResponse, Error>, "queryKey" | "queryFn">),
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao buscar configurações",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error]);

  return {
    shareConfigs: query.data?.data ?? [],
    totalPages: query.data?.totalPage ?? 0,
    totalItems: query.data?.totalItems ?? 0,
    currentPage: query.data?.page ?? filters?.page ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useReceivableShareConfig(
  id?: string,
  options?: Omit<UseQueryOptions<ReceivableShareConfig, Error>, "queryKey" | "queryFn">,
) {
  const queryKey = id ? receivableShareConfigKeys.detail(id) : [...receivableShareConfigKeys.details(), ''];
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => {
      if (!id) throw new Error("ID da configuração não informado");
      return receivableShareConfigApi.get(id, signal);
    },
    enabled: Boolean(id),
    ...(options as Omit<UseQueryOptions<ReceivableShareConfig, Error>, "queryKey" | "queryFn">),
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar configuração",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error]);

  return {
    shareConfig: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useCreateReceivableShareConfig(
  options?: UseMutationOptions<ReceivableShareConfig, Error, CreateReceivableShareConfigPayload>,
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ["receivable-share-configs", "create"],
    mutationFn: receivableShareConfigApi.create,
    ...mutationOptions,
    onSuccess: (shareConfig, variables, onMutateResult, context) => {
      toast({
        title: "Configuração criada com sucesso",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: receivableShareConfigKeys.all });
      providedOnSuccess?.(shareConfig, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: "Erro ao criar configuração",
        description: error.message,
        variant: "destructive",
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });

  return {
    createShareConfig: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useUpdateReceivableShareConfig(
  options?: UseMutationOptions<ReceivableShareConfig, Error, { id: string; data: UpdateReceivableShareConfigPayload }>,
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ["receivable-share-configs", "update"],
    mutationFn: ({ id, data }: { id: string; data: UpdateReceivableShareConfigPayload }) =>
      receivableShareConfigApi.update(id, data),
    ...mutationOptions,
    onSuccess: (shareConfig, variables, onMutateResult, context) => {
      toast({
        title: "Configuração atualizada com sucesso",
        variant: "success",
      });
      // queryClient.invalidateQueries({ queryKey: ["receivable-share-configs", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: receivableShareConfigKeys.all });
      providedOnSuccess?.(shareConfig, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: "Erro ao atualizar configuração",
        description: error.message,
        variant: "destructive",
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });

  return {
    updateShareConfig: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useDeleteReceivableShareConfig(
  options?: UseMutationOptions<{ id: string; deletedAt: string }, Error, string>,
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ["receivable-share-configs", "delete"],
    mutationFn: receivableShareConfigApi.remove,
    ...mutationOptions,
    onSuccess: (response, id, onMutateResult, context) => {
      toast({
        title: "Configuração removida com sucesso",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: receivableShareConfigKeys.all });
      providedOnSuccess?.(response, id, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: "Erro ao remover configuração",
        description: error.message,
        variant: "destructive",
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });

  return {
    deleteShareConfig: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}
